import ErrorFallback from 'components/error-fallback/ErrorFallback';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { CUSTOM_RESULT_KEYS } from 'constants/constants';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { EPD_DICTIONARY_CODES, INDICATOR_GROUP_CODES } from 'services/api/constants';
import {
  useCreateLcaCustomResult,
  useDeleteLcaCustomResult,
  useUpdateLcaCustomResult,
} from 'services/api/mutationsLcaResults';
import {
  useDictionaryValues,
  useEPDDefinitionInformation,
  useEpdLcaCustomResults,
  useEpdLcaResults,
  useEpdScenarios,
  useGetInfrastructureAndCapitalGoods,
  useGetLCASpecification,
} from 'services/api/queries';
import { LcaCustomResultModel, LcaResultModel, LCASpecificationModel, Option } from 'types/types';
import { getOptionsFromDictionary } from 'util/utils';

import { useUpsertLCASpecification } from 'services/api/mutations';
import { AddCustomBlockButton } from '../buttons/AddCustomBlockButton';
import WizardTextAreaComponent from '../epd-wizard-inputs/WizardTextArea';
import { EPDProductClassificationId, YES_NO } from './constants';
import LcaResultsGridAdditionalConstruction from './grids/lca-results/LcaResultsGridAdditionalConstruction';
import LcaResultsGridAdditionalMandatory from './grids/lca-results/LcaResultsGridAdditionalMandatory';
import LcaResultsGridAdditionalVoluntary from './grids/lca-results/LcaResultsGridAdditionalVoluntary';
import LcaResultsGridCoreConstruction from './grids/lca-results/LcaResultsGridCoreConstruction';
import LcaResultsGridOutputFlowConstruction from './grids/lca-results/LcaResultsGridOutputFlowConstruction';
import LcaResultsGridResourceConstruction from './grids/lca-results/LcaResultsGridResourceConstruction';
import LcaResultsGridWasteConstruction from './grids/lca-results/LcaResultsGridWasteConstruction';
import LcaGridErrorsContext from './grids/lca-results/util/LcaGridErrorsContext';
import { LcaImportContextProvider } from './grids/lca-results/util/LcaImportContext';
import ValidateErrorsContainer from './grids/lca-results/ValidateErrorsContainer';
import useIsReadOnlyMode from './hooks/useIsReadOnlyMode';
import usePcrV2, { usePcrV2Loading } from './hooks/usePcrV2';
import GridExpandablePanel from './panels/expandable/GridExpandablePanel';
import ImportLcaResults from './panels/import-lca-results/ImportLcaResults';
import LcaResultsAutofillNd from './panels/lca-support/LcaResultsAutofillNd';
import LcaResultsAutofillNdAdditionalVoluntary15804 from './panels/lca-support/LcaResultsAutofillNdAdditionalVoluntary15804';
import LcaCustomResultsSection from './sections/lcaResults/LcaCustomResultsSection';
import SkeletonLcaResultsTab from './skeletons/SkeletonLcaResultsTab';
import ReactGridEditorAccessGate from './ReactGridEditor/ReactGridEditorAccessGate';
import ReactGridEditor from './ReactGridEditor/ReactGridEditor';
import { ReactGridEditorContainer, ReactGridEditorLabel } from './ReactGridEditor/ReactGridEditor.styled';
import { useGridSync } from './ReactGridEditor/useGridSync';
import { CustomResultValidateButtonPressed } from './Step3';

const LcaResultsTab: React.FunctionComponent<{
  epdVersionId: string;
  validationState: any;
  isCore15804ValidateBtnPressed: boolean;
  setIsCore15804ValidateBtnPressed: any;
  isAdditionalMandatoryValidateBtnPressed: boolean;
  setIsAdditionalMandatoryValidateBtnPressed: any;
  isAdditionalVoluntary15804ValidateBtnPressed: boolean;
  setIsAdditionalVoluntary15804ValidateBtnPressed: any;
  isAdditionalVoluntaryValidateBtnPressed: boolean;
  setIsAdditionalVoluntaryValidateBtnPressed: any;
  isResource15804ValidateBtnPressed: boolean;
  setIsResource15804ValidateBtnPressed: any;
  isWaste15804ValidateBtnPressed: boolean;
  setIsWaste15804ValidateBtnPressed: any;
  isOutputFlow15804ValidateBtnPressed: boolean;
  setIsOutputFlow15804ValidateBtnPressed: any;
  customResultButtonPressed: CustomResultValidateButtonPressed[];
  setCustomResultButtonPressed: any;
}> = ({
  epdVersionId,
  validationState,
  isCore15804ValidateBtnPressed,
  setIsCore15804ValidateBtnPressed,
  isAdditionalMandatoryValidateBtnPressed,
  setIsAdditionalMandatoryValidateBtnPressed,
  isAdditionalVoluntary15804ValidateBtnPressed,
  setIsAdditionalVoluntary15804ValidateBtnPressed,
  isAdditionalVoluntaryValidateBtnPressed,
  setIsAdditionalVoluntaryValidateBtnPressed,
  isResource15804ValidateBtnPressed,
  setIsResource15804ValidateBtnPressed,
  isWaste15804ValidateBtnPressed,
  setIsWaste15804ValidateBtnPressed,
  isOutputFlow15804ValidateBtnPressed,
  setIsOutputFlow15804ValidateBtnPressed,
  customResultButtonPressed,
  setCustomResultButtonPressed,
}) => {
  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;
  const lcaResultsInformation = useEpdLcaResults(epdVersionId).data;
  const lcaCustomResults = useEpdLcaCustomResults(epdVersionId).data;
  const infrastructureAndCapitalGoods = useGetInfrastructureAndCapitalGoods(epdVersionId).data;
  const createLcaCustomResultsMutation = useCreateLcaCustomResult();
  const deleteLcaCustomResultsMutation = useDeleteLcaCustomResult();
  const updateLcaCustomResultsMutation = useUpdateLcaCustomResult(epdVersionId);
  const lcaSpecification = useGetLCASpecification(epdVersionId!).data;
  const updateMutationLCASpecification = useUpsertLCASpecification(epdVersionId);
  const scenarios = useEpdScenarios(epdVersionId).data;
  const dictionariesQuery = useDictionaryValues();
  const isPcrV2Loading = usePcrV2Loading();
  const isPcrV2 = usePcrV2();
  const isReadOnly = useIsReadOnlyMode();
  const isPcrV2Selected = usePcrV2();

  const isLoading =
    epdDefinitionInformation === undefined ||
    lcaResultsInformation === undefined ||
    lcaCustomResults === undefined ||
    scenarios === undefined ||
    infrastructureAndCapitalGoods === undefined ||
    dictionariesQuery.data === undefined ||
    isPcrV2Loading ||
    lcaSpecification === undefined ||
    validationState === undefined;
  if (isLoading) {
    return <SkeletonLcaResultsTab />;
  }

  const onChangeLCASpecification = async (propertyName: string, val: any) => {
    const newData = { ...lcaSpecification, [propertyName]: val } as LCASpecificationModel;

    if (propertyName === 'lcaSoftwareId') {
      newData.lcaSoftwareVersionId = undefined;
    }

    updateMutationLCASpecification.mutate(newData);
  };

  const autofillModuleFields: Array<keyof LcaResultModel> = [
    'a1A2A3',
    'a4',
    'a5',
    'b1',
    'b2',
    'b3',
    'b4',
    'b5',
    'b6',
    'b7',
    'c1',
    'c2',
    'c3',
    'c4',
    'd',
  ];

  const additionalVoluntary15804Rows = lcaResultsInformation.lcaResults.filter(
    (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALVOLUNTARY15804
  );

  const isAdditionalVoluntary15804AllFilled =
    additionalVoluntary15804Rows.length > 0 &&
    additionalVoluntary15804Rows.every((r) => r.id && autofillModuleFields.every((key) => !!r[key]));

  const errors = validationState.errors.lcaResults;

  const hasNumericValues = (r: LcaResultModel) =>
    r.id &&
    (
      ['a1', 'a2', 'a3', 'a4', 'a5', 'c1', 'c2', 'c3', 'c4', 'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'd'] as Array<
        keyof LcaResultModel
      >
    ).some((key) => r[key] && r[key] !== 'ND');

  const scenariosLookup = scenarios
    .filter((x) => !x.isDefaultScenario)
    .map((s) => {
      return {
        value: s.id,
        label: s.name ?? '<unnamed>',
      } as Option;
    });

  const handleCreateCustomLcaResults = () => {
    createLcaCustomResultsMutation.mutate({ epdVersionId });
  };

  const confirmDeleteCustomLcaResults = (id: string) => {
    confirmDelete({
      caption: `Are you sure you want to delete custom results section?`,
      onConfirm: () => deleteLcaCustomResultsMutation.mutate(id),
    });
  };

  const handleOnChangeCustomLcaResult = (customResult: LcaCustomResultModel) => (propertyName: string, val: any) => {
    let updateData = { ...customResult, [propertyName]: val };
    updateLcaCustomResultsMutation.mutate(updateData);
  };

  const additionalPerformanceResultsTypeLookup = getOptionsFromDictionary(
    dictionariesQuery.data,
    EPD_DICTIONARY_CODES.ADDITIONAL_LCA_RESULTS_TYPE
  );

  const additionalPerformanceResultsTypeLookupFiltered = isPcrV2
    ? additionalPerformanceResultsTypeLookup
    : additionalPerformanceResultsTypeLookup.filter((x) => x.value !== CUSTOM_RESULT_KEYS.BIOGAS);

  const additionalPerformanceResultsTypes = additionalPerformanceResultsTypeLookupFiltered.map((x) => {
    return {
      ...x,
      isDisabled:
        epdDefinitionInformation.epdClassification === EPDProductClassificationId.SINGLE_PRODUCT_ID &&
        (x.value == CUSTOM_RESULT_KEYS.BEST || x.value == CUSTOM_RESULT_KEYS.WORST),
    };
  });

  return (
    <LcaImportContextProvider>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <ImportLcaResults
          epdVersionId={epdVersionId}
          hasSomeResultsAlready={lcaResultsInformation.lcaResults.some((r) => r.id)}
        />

        {!isReadOnly && (
          <LcaResultsAutofillNd
            epdVersionId={epdVersionId}
            hasSomeResultsAlready={lcaResultsInformation.lcaResults.some(hasNumericValues)}
          />
        )}

        <GridExpandablePanel
          title="Mandatory environmental performance indicators according to EN 15804"
          error={errors?.core15804}
        >
          <LcaGridErrorsContext.Provider value={isCore15804ValidateBtnPressed && errors?.core15804}>
            {errors?.core15804 && <ValidateErrorsContainer onValidate={() => setIsCore15804ValidateBtnPressed(true)} />}
            <LcaResultsGridCoreConstruction
              epdVersionId={epdVersionId}
              rows={lcaResultsInformation?.lcaResults?.filter(
                (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.CORE15804
              )}
              isAdditionalDisclaimerVisible={infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
            />
          </LcaGridErrorsContext.Provider>
        </GridExpandablePanel>

        <GridExpandablePanel
          title="Additional mandatory environmental performance indicators"
          tooltip="GWP-GHG accounts for all greenhouse gases except biogenic carbon dioxide uptake and emissions and biogenic carbon stored in the product. As such, the indicator is identical to GWP-total except that the characterisation factor for biogenic CO₂ is set to zero."
          error={errors?.additionalMandatory}
        >
          <LcaGridErrorsContext.Provider value={isAdditionalMandatoryValidateBtnPressed && errors?.additionalMandatory}>
            {errors?.additionalMandatory && (
              <ValidateErrorsContainer onValidate={() => setIsAdditionalMandatoryValidateBtnPressed(true)} />
            )}
            <LcaResultsGridAdditionalMandatory
              epdVersionId={epdVersionId}
              rows={lcaResultsInformation?.lcaResults?.filter(
                (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALMANDATORY
              )}
            />
          </LcaGridErrorsContext.Provider>
        </GridExpandablePanel>

        <GridExpandablePanel
          title="Additional voluntary environmental performance indicators according to EN 15804"
          error={errors?.additionalVoluntary15804}
        >
          {!isReadOnly && (
            <LcaResultsAutofillNdAdditionalVoluntary15804
              epdVersionId={epdVersionId}
              disabled={isAdditionalVoluntary15804AllFilled}
            />
          )}
          <LcaGridErrorsContext.Provider
            value={isAdditionalVoluntary15804ValidateBtnPressed && errors?.additionalVoluntary15804}
          >
            {errors?.additionalVoluntary15804 && (
              <ValidateErrorsContainer onValidate={() => setIsAdditionalVoluntary15804ValidateBtnPressed(true)} />
            )}
            <LcaResultsGridAdditionalConstruction
              epdVersionId={epdVersionId}
              rows={lcaResultsInformation?.lcaResults?.filter(
                (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALVOLUNTARY15804
              )}
              isAdditionalDisclaimerVisible={infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
            />
          </LcaGridErrorsContext.Provider>
        </GridExpandablePanel>

        <GridExpandablePanel
          title="Additional voluntary environmental performance indicators"
          error={errors?.additionalVoluntary || validationState.errors?.justificationForInclusion}
          maximizableContent={
            <>
              <LcaGridErrorsContext.Provider value={isAdditionalVoluntaryValidateBtnPressed && errors?.additionalVoluntary}>
                {errors?.additionalVoluntary && (
                  <ValidateErrorsContainer onValidate={() => setIsAdditionalVoluntaryValidateBtnPressed(true)} />
                )}
                <LcaResultsGridAdditionalVoluntary epdVersionId={epdVersionId} />
              </LcaGridErrorsContext.Provider>
            </>
          }
          additionalContent={
            isPcrV2Selected && (
              <WizardTextAreaComponent
                label="Justification for inclusion"
                placeholder="Type here"
                value={lcaSpecification?.justificationForInclusion}
                onChanged={onChangeLCASpecification}
                name="justificationForInclusion"
                error={validationState.errors?.justificationForInclusion}
              />
            )
          }
        />

        <GridExpandablePanel title="Resource use indicators according to EN 15804" error={errors?.resource15804}>
          <LcaGridErrorsContext.Provider value={isResource15804ValidateBtnPressed && errors?.resource15804}>
            {errors?.resource15804 && (
              <ValidateErrorsContainer onValidate={() => setIsResource15804ValidateBtnPressed(true)} />
            )}
            <LcaResultsGridResourceConstruction
              epdVersionId={epdVersionId}
              rows={lcaResultsInformation?.lcaResults?.filter(
                (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.RESOURCE15804
              )}
            />
          </LcaGridErrorsContext.Provider>
        </GridExpandablePanel>

        <GridExpandablePanel title="Waste indicators according to EN 15804" error={errors?.waste15804}>
          <LcaGridErrorsContext.Provider value={isWaste15804ValidateBtnPressed && errors?.waste15804}>
            {errors?.waste15804 && <ValidateErrorsContainer onValidate={() => setIsWaste15804ValidateBtnPressed(true)} />}
            <LcaResultsGridWasteConstruction
              epdVersionId={epdVersionId}
              rows={lcaResultsInformation?.lcaResults?.filter(
                (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.WASTE15804
              )}
            />
          </LcaGridErrorsContext.Provider>
        </GridExpandablePanel>

        <GridExpandablePanel title="Output flow indicators according to EN 15804" error={errors?.outputFlow15804}>
          <LcaGridErrorsContext.Provider value={isOutputFlow15804ValidateBtnPressed && errors?.outputFlow15804}>
            {errors?.outputFlow15804 && (
              <ValidateErrorsContainer onValidate={() => setIsOutputFlow15804ValidateBtnPressed(true)} />
            )}
            <LcaResultsGridOutputFlowConstruction
              epdVersionId={epdVersionId}
              rows={lcaResultsInformation?.lcaResults?.filter(
                (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.OUTPUTFLOW15804
              )}
            />
            <ReactGridEditorAccessGate>
              <OutputFlowReactGridEditor
                epdVersionId={epdVersionId}
                rows={
                  lcaResultsInformation?.lcaResults?.filter(
                    (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.OUTPUTFLOW15804
                  ) || []
                }
              />
            </ReactGridEditorAccessGate>
          </LcaGridErrorsContext.Provider>
        </GridExpandablePanel>

        <LcaCustomResultsSection
          epdVersionId={epdVersionId}
          lcaCustomResults={lcaCustomResults}
          additionalPerformanceResultsTypes={additionalPerformanceResultsTypes}
          scenariosLookup={scenariosLookup}
          isReadOnly={isReadOnly}
          errors={errors}
          customResultButtonPressed={customResultButtonPressed}
          setCustomResultButtonPressed={setCustomResultButtonPressed}
          onDeleteCustomResult={confirmDeleteCustomLcaResults}
          onChangedCustomResult={handleOnChangeCustomLcaResult}
        />

        {!isReadOnly && (
          <AddCustomBlockButton
            onClick={handleCreateCustomLcaResults}
            caption="Add additional environmental performance results"
          />
        )}
      </ErrorBoundary>
    </LcaImportContextProvider>
  );
};

/**
 * Wrapper component for the ReactGrid editor that manages sync state
 * for output flow indicators.
 */
const OutputFlowReactGridEditor: React.FC<{ epdVersionId: string; rows: LcaResultModel[] }> = ({ epdVersionId, rows }) => {
  const { localRows, handleRowsChange, isSyncing, syncError } = useGridSync({
    epdVersionId,
    initialRows: rows,
  });

  return (
    <ReactGridEditorContainer>
      <ReactGridEditorLabel>
        ⚙ Test excel-like editor <span>Edit cells directly — changes sync to the grid above</span>
      </ReactGridEditorLabel>
      {isSyncing && <div className="reactgrid-sync-indicator">Saving...</div>}
      {syncError && <div className="reactgrid-sync-error">{syncError}</div>}
      <ReactGridEditor rows={localRows} onRowsChange={handleRowsChange} />
    </ReactGridEditorContainer>
  );
};

export default LcaResultsTab;
